import { defineStore } from "pinia";
import { ref, computed } from "vue";
import CTGService from "../services/CTGService";
import { ICTG } from "../models/ctg";
import { ICTGApiResponse } from "../models/api/ctgApiResponse";
import { ICTGForm } from "../models/forms/ctgForm";
import { useParameterStore } from "./parameter.store";
import { useThirdPartiesStore } from "./thirdParties.store";
import signalRManager from "../helper/signalRManager";
import IPayloadStore from "../models/api/payloadStore";

//Store Pinia CTG
export const useCTG = defineStore("ctg", () => {
    // State
    const ctg = ref<ICTG[]>([]);
    const listCTG = ref<ICTG[]>([]);
    const currentCTG = ref<ICTG>({} as ICTG);
    const itemForm = ref<ICTGForm>({} as ICTGForm);

    // Actions - Hydration
    /**
     * Hydrate CTGs from API response
     */
    async function hydrateCTGs(ctgs: ICTGApiResponse[]): Promise<ICTG[]> {
        if (ctgs.length === 0) return [];

        // 1. Get stores
        const parameterStore = useParameterStore();
        const thirdPartiesStore = useThirdPartiesStore();

        // 2. Load in parallel if needed
        const loadPromises: Promise<unknown>[] = [];
        if (parameterStore.datas.length === 0) {
            loadPromises.push(parameterStore.load({ page: 1, pageSize: 0 }));
        }
        if (!thirdPartiesStore.loadedAll) {
            loadPromises.push(thirdPartiesStore.load({ page: 1, pageSize: 0 }));
        }
        if (loadPromises.length > 0) {
            await Promise.all(loadPromises);
        }

        // 3. Create Maps for O(1) lookups
        const parameterMap = new Map(parameterStore.datas.map(p => [p.id, p]));
        const thirdPartyMap = new Map(thirdPartiesStore.datas.map(t => [t.id, t]));

        // 4. Hydrate all entities in one pass
        const models: ICTG[] = [];
        for (const ctgData of ctgs) {
            const model = { ...ctgData } as unknown as ICTG;

            // Link references (O(1) via Map)
            model.breed = parameterMap.get(ctgData.breedId) ?? model.breed;
            model.geneticType = parameterMap.get(ctgData.geneticTypeId) ?? model.geneticType;
            model.owner = thirdPartyMap.get(ctgData.ownerId) ?? model.owner;
            model.provider = thirdPartyMap.get(ctgData.providerId) ?? model.provider;

            models.push(model);
        }

        return models;
    }

    // Getters
    /**
     * Renvoie la liste des codes CTG trié par ordre
     */
    const getListSchema = computed(() => {
        return listCTG.value.sort((a, b) => {
            const nameA = a.code.toUpperCase();
            const nameB = b.code.toUpperCase();
            if (nameA < nameB) return -1;
            if (nameA > nameB) return 1;
            return 0;
        });
    });

    // Actions
    /**
     * Recherche des CTG en fonction de son code
     * @param code Code CTG recherché
     * @returns Liste CTG correspondants
     */
    async function loadCTG(code: string) {
        const result = await CTGService.getCTGByCode(code);

        if (result == null) {
            return { value: [], error: result };
        } else {
            ctg.value = await hydrateCTGs(result as ICTGApiResponse[]);
        }

        return { value: ctg.value, error: "" };
    }

    /**
     * Recherche des CTG en fonction de son id
     * @param id ID CTG recherché
     * @returns CTG correspondant
     */
    async function getCTGId(id: number) {
        const result = await CTGService.getCTGById(id);

        if (result.error) {
            return { value: [], error: result.error };
        } else {
            const hydratedCTGs = await hydrateCTGs([result.data[0]] as ICTGApiResponse[]);
            return { value: hydratedCTGs[0], error: "" };
        }
    }

    /**
     * Remise à 0 du CTG en cours de modification
     * @returns CTG mis à jour
     */
    async function resetCurrentCTG() {
        currentCTG.value = {
            id: 0,
            code: null,
            active: false,
            breedId: 0,
            geneticTypeId: 0,
            ownerId: 0,
            providerId: 0,
            breed: null,
            owner: null,
            provider: null,
            geneticType: null,
        } as unknown as ICTG;

        return currentCTG.value;
    }

    /**
     * Récupère et met à jour le CTG en cours de modification
     * @param id ID du CTG
     * @returns CTG Courant
     */
    async function setCurrentCTG(id: number | null): Promise<ICTG> {
        if (id === null) {
            currentCTG.value = {} as ICTG;
        } else {
            const dataCTG = await CTGService.getCTGById(id);

            if (dataCTG) {
                const [hydratedCTG] = await hydrateCTGs([dataCTG.data[0]] as ICTGApiResponse[]);
                currentCTG.value = hydratedCTG;
                itemForm.value = toForm(hydratedCTG);
            }
        }

        return currentCTG.value;
    }

    /**
     * Recherche de l'intégralité des codes CTG
     * @returns Liste des codes CTG
     */
    async function loadListCTG() {
        const result = await CTGService.getListCTG();

        if (result == null) {
            return { value: [], error: 'Error services' };
        } else {
            listCTG.value = await hydrateCTGs(result as ICTGApiResponse[]);
        }

        return { value: listCTG.value, error: "" };
    }

    /**
     * Alias pour compatibilité avec le pattern standard de chargement
     * Utilisé par storeLoader pour précharger les CTGs au démarrage
     */
    async function load(filter?: { page: number; pageSize: number }) {
        return await loadListCTG();
    }

    /**
     * Enregistre le Code CTG sur le serveur
     * @param newCTG Nouveau CTG
     * @returns Nouveau code CTG
     */
    async function createCTG(newCTG: ICTG) {
        const result = await CTGService.createCTG(newCTG);

        if (!result.err) {
            const [hydratedCTG] = await hydrateCTGs([result.data] as ICTGApiResponse[]);
            listCTG.value.push(hydratedCTG);
        }

        return result;
    }

    /**
     * Convertit un CTG vers formulaire
     */
    function toForm(ctgData: ICTG): ICTGForm {
        const form = {} as ICTGForm;

        Object.assign(form, ctgData);
        form.breedId = ctgData.breedId;
        form.geneticTypeId = ctgData.geneticTypeId;
        form.ownerId = ctgData.ownerId;
        form.providerId = ctgData.providerId;
        return form;
    }

    /**
     * Met à jour le code CTG et modifie le store
     * @param ctgData CTG à mettre à jour
     * @returns code CTG modifié
     */
    async function updateCTGInList(ctgData: ICTG) {
        const ctgUpdated = await CTGService.updateCTG(ctgData);

        if (!ctgUpdated.err) {
            const [hydratedCTG] = await hydrateCTGs([ctgUpdated.data] as ICTGApiResponse[]);
            const index = listCTG.value.findIndex(({ id }) => id === ctgData.id);

            if (index >= 0) {
                listCTG.value.splice(index, 1, hydratedCTG);
            } else {
                listCTG.value.push(hydratedCTG);
            }
        }

        return ctgUpdated;
    }

    /**
     * Initialise un objet vide pour le formulaire
     */
    async function newForm() {
        itemForm.value = {
            id: 0,
            code: '',
            isActive: true,
            breedId: 0,
            providerId: 0,
            geneticTypeId: 0,
            ownerId: 0
        } as ICTGForm;
        return itemForm.value;
    }

    return {
        // State
        ctg,
        listCTG,
        currentCTG,
        itemForm,
        // Getters
        getListSchema,
        // Actions
        hydrateCTGs,
        load,
        loadCTG,
        getCTGId,
        resetCurrentCTG,
        setCurrentCTG,
        loadListCTG,
        createCTG,
        toForm,
        updateCTGInList,
        newForm
    };
});

signalRManager.connection.on("MajCTG", async (payload: IPayloadStore) => {
    const store = useCTG();
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                const [hydrated] = await store.hydrateCTGs([payload.data as ICTGApiResponse]);
                store.datas.push(hydrated);
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                const [hydrated] = await store.hydrateCTGs([payload.data as ICTGApiResponse]);
                store.datas.splice(idx, 1, hydrated);
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);
            break;
        }
    }
});
